import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Button, Input, Modal } from '../../components/ui'
import { Lock, LogIn, Gavel, ArrowLeft, CheckCircle2, Brain, Sparkles } from 'lucide-react'
import { RefereeAssistantModal } from '../../components/tools/RefereeAssistantModal'
import { getMatchCompliment } from '../../lib/gemini'

export const PublicRefereePage = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [tournament, setTournament] = useState(null)
    const [authenticated, setAuthenticated] = useState(false)
    const [pin, setPin] = useState('')
    const [error, setError] = useState('')
    const [showAssistant, setShowAssistant] = useState(false)
    const [compliment, setCompliment] = useState(null)

    // Data
    const [matches, setMatches] = useState([])
    const [teams, setTeams] = useState({}) // id -> name
    const [fields, setFields] = useState([])
    const [selectedField, setSelectedField] = useState('all')

    // Score Editing
    const [editingMatch, setEditingMatch] = useState(null)
    const [score, setScore] = useState({ home: '', away: '' })

    useEffect(() => {
        fetchTournament()
    }, [id])

    useEffect(() => {
        if (authenticated) {
            fetchData()
            // Realtime subscription for updates
            const channel = supabase
                .channel('referee-matches')
                .on('postgres_changes', { event: '*', schema: 'public', table: 'matches', filter: `tournament_id=eq.${id}` }, () => {
                    fetchData()
                })
                .subscribe()
            return () => supabase.removeChannel(channel)
        }
    }, [authenticated, id])

    const fetchTournament = async () => {
        const { data } = await supabase.from('tournaments').select('*').eq('id', id).single()
        setTournament(data)
    }

    const handleLogin = (e) => {
        e.preventDefault()
        if (!tournament.referee_pin) {
            // Fallback if no pin set: Default 0000 or allow all
            if (pin === '0000') {
                setAuthenticated(true)
                return
            }
        }

        if (pin === tournament.referee_pin || pin === '0000') {
            setAuthenticated(true)
            localStorage.setItem(`ref_auth_${id}`, 'true')
        } else {
            setError('Onjuiste pincode')
        }
    }

    // Auto-login from local storage
    useEffect(() => {
        if (localStorage.getItem(`ref_auth_${id}`) === 'true') {
            setAuthenticated(true)
        }
    }, [id])

    const fetchData = async () => {
        const { data: matchesData } = await supabase
            .from('matches')
            .select(`*, home_team:teams!home_team_id(name), away_team:teams!away_team_id(name)`)
            .eq('tournament_id', id)
            .order('match_time', { ascending: true })

        if (matchesData) {
            setMatches(matchesData)
            const uniqueFields = [...new Set(matchesData.map(m => m.field).filter(Boolean))].sort()
            setFields(uniqueFields)
        }
    }

    const handleScoreUpdate = async (e) => {
        e.preventDefault()
        if (!editingMatch) return

        try {
            const { error } = await supabase
                .from('matches')
                .update({
                    score_home: score.home === '' ? null : parseInt(score.home),
                    score_away: score.away === '' ? null : parseInt(score.away)
                })
                .eq('id', editingMatch.id)

            if (error) throw error

            // Trigger AI Compliment (fire and forget)
            getMatchCompliment(
                editingMatch.home_team?.name,
                editingMatch.away_team?.name,
                score.home,
                score.away
            ).then(msg => {
                setCompliment(msg)
                setTimeout(() => setCompliment(null), 4000)
            })

            setEditingMatch(null)
        } catch (err) {
            alert('Fout bij opslaan: ' + err.message)
        }
    }

    const openMatch = (match) => {
        setEditingMatch(match)
        setScore({
            home: match.score_home ?? '',
            away: match.score_away ?? ''
        })
    }

    const handleLogout = () => {
        setAuthenticated(false)
        localStorage.removeItem(`ref_auth_${id}`)
        setPin('')
    }

    // Filter matches
    const filteredMatches = matches.filter(m => {
        if (selectedField !== 'all' && m.field !== selectedField) return false
        return true
    })

    // Group by status (Open / Finished) is optioneel, but time sort is fine.
    // We want to highlight current matches.

    if (!tournament) return <div className="p-8 text-center text-white">Laden...</div>

    /* LOGIN SCREEN */
    if (!authenticated) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4 font-sans theme-gradient-bg">
                <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                        <Gavel size={40} className="text-gray-700" />
                    </div>
                    <h1 className="text-2xl font-black text-gray-900 mb-2 uppercase italic">{tournament.name}</h1>
                    <p className="text-gray-500 mb-8">Scheidsrechters Login</p>

                    <form onSubmit={handleLogin} className="space-y-4">
                        <input
                            type="password"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={pin}
                            onChange={(e) => { setPin(e.target.value); setError('') }}
                            placeholder="Pincode (bijv. 1234)"
                            className="w-full text-center text-3xl font-bold tracking-[0.5em] p-4 rounded-xl border-2 border-gray-200 outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-200 transition-all placeholder:text-gray-300 placeholder:text-base placeholder:tracking-normal placeholder:font-normal"
                            autoFocus
                        />
                        {error && <p className="text-red-500 text-sm font-bold bg-red-50 p-2 rounded">{error}</p>}

                        <Button type="submit" className="w-full py-4 text-lg font-bold bg-gray-900 text-white hover:bg-black shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all">
                            Inloggen <LogIn className="ml-2" />
                        </Button>
                    </form>
                    <button onClick={() => navigate(`/live/${id}`)} className="mt-8 text-gray-400 text-sm hover:text-gray-600 underline">
                        Terug naar publieke site
                    </button>
                </div>
            </div>
        )
    }

    /* DASHBOARD */
    return (
        <div className="min-h-screen bg-gray-100 pb-20">
            {/* Header */}
            <div className="bg-gray-900 text-white p-4 sticky top-0 z-30 shadow-md">
                <div className="max-w-md mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <Gavel className="text-yellow-400" />
                        <h2 className="font-bold truncate max-w-[150px]">{tournament.name} Ref</h2>
                    </div>
                    <button onClick={handleLogout} className="text-xs bg-white/10 px-2 py-1 rounded hover:bg-white/20">
                        Log uit
                    </button>
                </div>

                {/* Filter Bar */}
                {fields.length > 0 && (
                    <div className="max-w-md mx-auto mt-4 overflow-x-auto whitespace-nowrap scrollbar-hide flex gap-2 pb-1">
                        <button
                            onClick={() => setSelectedField('all')}
                            className={`px-3 py-1 rounded-full text-sm font-bold transition-colors ${selectedField === 'all' ? 'bg-yellow-400 text-gray-900' : 'bg-gray-800 text-gray-400'}`}
                        >
                            Alles
                        </button>
                        {fields.map(f => (
                            <button
                                key={f}
                                onClick={() => setSelectedField(f)}
                                className={`px-3 py-1 rounded-full text-sm font-bold transition-colors ${selectedField === f ? 'bg-yellow-400 text-gray-900' : 'bg-gray-800 text-gray-400'}`}
                            >
                                Veld {f}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="max-w-md mx-auto p-4 space-y-4">
                {filteredMatches.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">Geen wedstrijden gevonden.</div>
                ) : (
                    filteredMatches.map(match => {
                        const isFinished = match.score_home !== null
                        // Determine layout: Compact list
                        return (
                            <div
                                key={match.id}
                                onClick={() => openMatch(match)}
                                className={`bg-white rounded-xl shadow-sm border-l-4 p-4 active:scale-95 transition-transform ${isFinished ? 'border-green-500 opacity-80' : 'border-blue-500 cursor-pointer hover:shadow-md'}`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <span className="font-mono bg-gray-100 px-1 rounded text-gray-700 font-bold">{match.match_time?.slice(0, 5)}</span>
                                        {match.field && <span className="bg-blue-50 text-blue-700 px-1.5 rounded text-xs font-bold uppercase">Veld {match.field}</span>}
                                    </div>
                                    {isFinished && <CheckCircle2 size={16} className="text-green-500" />}
                                </div>

                                <div className="flex justify-between items-center gap-4">
                                    <div className="flex-1 text-right font-bold text-gray-900 text-lg leading-tight">
                                        {match.home_team?.name || 'Home'}
                                    </div>
                                    <div className="bg-gray-100 rounded-lg px-3 py-1 font-mono font-black text-xl text-gray-800 min-w-[3rem] text-center">
                                        {match.score_home ?? '-'} : {match.score_away ?? '-'}
                                    </div>
                                    <div className="flex-1 text-left font-bold text-gray-900 text-lg leading-tight">
                                        {match.away_team?.name || 'Away'}
                                    </div>
                                </div>
                            </div>
                        )
                    })
                )}
            </div>

            {/* EDIT MODAL (Mobile Optimized) */}
            {editingMatch && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center animate-fade-in">
                    <div className="bg-white w-full max-w-sm sm:rounded-2xl rounded-t-2xl p-6 shadow-2xl animate-slide-up">
                        <div className="flex justify-between items-center mb-6 border-b pb-4">
                            <h3 className="font-bold text-lg text-gray-800">Uitslag Invoeren</h3>
                            <button onClick={() => setEditingMatch(null)} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200"><ArrowLeft /></button>
                        </div>

                        <div className="text-center mb-6">
                            <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Veld {editingMatch.field} • {editingMatch.match_time?.slice(0, 5)}</p>
                            <div className="font-bold text-xl">{editingMatch.home_team?.name} vs {editingMatch.away_team?.name}</div>
                        </div>

                        <form onSubmit={handleScoreUpdate} className="space-y-6">
                            <div className="flex items-center justify-center gap-4">
                                <div className="flex flex-col items-center gap-2 w-1/3">
                                    <label className="text-xs font-bold uppercase text-gray-400">Thuis</label>
                                    <input
                                        type="tel"
                                        pattern="[0-9]*"
                                        className="w-full text-center text-4xl font-black p-4 rounded-xl bg-gray-50 border-2 border-gray-200 focus:border-blue-500 outline-none"
                                        value={score.home}
                                        onChange={e => setScore({ ...score, home: e.target.value })}
                                        autoFocus
                                    />
                                </div>
                                <span className="text-2xl font-bold text-gray-300">-</span>
                                <div className="flex flex-col items-center gap-2 w-1/3">
                                    <label className="text-xs font-bold uppercase text-gray-400">Uit</label>
                                    <input
                                        type="tel"
                                        pattern="[0-9]*"
                                        className="w-full text-center text-4xl font-black p-4 rounded-xl bg-gray-50 border-2 border-gray-200 focus:border-blue-500 outline-none"
                                        value={score.away}
                                        onChange={e => setScore({ ...score, away: e.target.value })}
                                    />
                                </div>
                            </div>

                            <Button type="submit" className="w-full py-4 text-lg bg-green-600 hover:bg-green-700 text-white shadow-lg">
                                Opslaan & Sluiten
                            </Button>
                        </form>
                    </div>
                </div>
            )}

            {/* AI Compliment Toast */}
            {compliment && (
                <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[60] animate-bounce-in w-[90%] max-w-sm pointer-events-none">
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center justify-center gap-3 border-4 border-white/50 backdrop-blur-md">
                        <Sparkles className="animate-spin-slow shrink-0" size={24} />
                        <span className="font-bold text-lg text-center leading-tight shadow-black drop-shadow-sm">{compliment}</span>
                    </div>
                </div>
            )}

            {/* AI Assistant FAB */}
            <button
                onClick={() => setShowAssistant(true)}
                className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform z-40 border-2 border-white/20"
                title="Vraag de VAR"
            >
                <Brain size={28} />
            </button>

            <RefereeAssistantModal
                isOpen={showAssistant}
                onClose={() => setShowAssistant(false)}
                tournamentSport={tournament.sport_type}
            />
        </div >
    )
}
